<template>
  <div class="m-2">
    <h1 class="mainheader d-flex justify-content-left p-2">
      <span>Chromosome Map Tool</span>
      <img
        src="@/assets/diamond.svg"
        height="20px"
        alt="Premium"
        class="ml-2"
      />
    </h1>

    <div class="pl-2 text-left">
      <p>
        Draw the 5 Arabidopsis chromosomes with the position of a list of
        genes. Enter one AGI locus identifier per line (e.g. At1g01010).
      </p>
      <p>
        Add an alternate display name after the locus (e.g.
        <code>At1g01010 hello</code>) to show it instead of the locus name. A
        dot as the name (<code>At1g01010 .</code>) draws only a tick mark, which
        is useful for many loci. A number between -5 and 5 after the locus is
        an expression value; with the tick color "variable" it sets the tick
        color. The zoom factor makes the chromosomes smaller (&lt;100) or
        larger (&gt;100); 100 is 50,000 bp per pixel. Enter at most
        {{ maxLoci }} loci.
      </p>
    </div>

    <div class="container p-2 border rounded shadow-sm form-bg">
      <form @submit.prevent="handleSubmit" @reset.prevent="handleReset">
        <fieldset class="border border-dark rounded p-2">
          <legend class="fieldset-title">Enter AGI locus identifiers</legend>
          <div class="form-group row">
            <label for="cmInput" class="col-sm-4 col-form-label text-left"
              >Loci:</label
            >
            <div class="col-sm-8">
              <textarea
                id="cmInput"
                v-model="form.input"
                class="form-control"
                rows="10"
                :placeholder="inputPlaceholder"
              ></textarea>
            </div>
          </div>
          <div class="form-group row">
            <label for="cmFile" class="col-sm-4 col-form-label text-left"
              >Or upload a file:</label
            >
            <div class="col-sm-8 text-left">
              <input
                id="cmFile"
                ref="fileInput"
                type="file"
                class="form-control-file"
                @change="handleFileUpload"
              />
            </div>
          </div>
        </fieldset>

        <fieldset class="border border-dark rounded p-2 mt-4">
          <legend class="fieldset-title">Display</legend>
          <div class="form-group row">
            <label for="cmZoom" class="col-sm-4 col-form-label text-left"
              >Zoom factor:</label
            >
            <div class="col-sm-8">
              <input
                id="cmZoom"
                v-model.number="form.zoom"
                type="number"
                min="1"
                max="1000"
                step="1"
                class="form-control"
              />
            </div>
          </div>
          <div class="form-group row">
            <label for="cmTitle" class="col-sm-4 col-form-label text-left"
              >Title (optional):</label
            >
            <div class="col-sm-8">
              <input
                id="cmTitle"
                v-model="form.title"
                type="text"
                class="form-control"
              />
            </div>
          </div>
          <div class="form-group row">
            <label for="cmChrColor" class="col-sm-4 col-form-label text-left"
              >Chromosome color:</label
            >
            <div class="col-sm-8">
              <select id="cmChrColor" v-model="form.chrColor" class="form-control">
                <option v-for="o in chrColorOptions" :key="o.value" :value="o.value">
                  {{ o.text }}
                </option>
              </select>
            </div>
          </div>
          <div class="form-group row">
            <label for="cmTickColor" class="col-sm-4 col-form-label text-left"
              >Tick color:</label
            >
            <div class="col-sm-8">
              <select id="cmTickColor" v-model="form.tickColor" class="form-control">
                <option v-for="o in tickColorOptions" :key="o.value" :value="o.value">
                  {{ o.text }}
                </option>
              </select>
            </div>
          </div>
        </fieldset>

        <div class="form-group row">
          <div class="col-sm-6 offset-sm-1 d-flex justify-content-left mt-2">
            <button
              type="submit"
              class="btn btn-primary"
              :disabled="loading"
              :aria-busy="loading ? 'true' : 'false'"
            >
              <span
                v-if="loading"
                class="spinner-border spinner-border-sm mr-2"
                aria-hidden="true"
              ></span>
              Display on chromosomes
            </button>
            <button
              type="reset"
              class="ml-4 btn btn-secondary"
              :disabled="loading"
            >
              Reset
            </button>
          </div>
        </div>
      </form>
    </div>

    <div class="sr-only" role="status" aria-live="polite">
      {{ statusMessage }}
    </div>

    <div v-if="errorMessage" class="alert alert-danger mt-3 text-left" role="alert">
      {{ errorMessage }}
    </div>

    <div v-if="result" class="container p-2 border rounded shadow-sm mt-3 text-left">
      <div class="d-flex justify-content-between align-items-center flex-wrap">
        <h2 class="mainheader m-0">{{ shownTitle }}</h2>
        <div>
          <button type="button" class="btn btn-outline-secondary btn-sm" @click="downloadSvg">
            Download SVG
          </button>
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm ml-2"
            @click="downloadPng"
          >
            Download PNG
          </button>
        </div>
      </div>
      <p class="small note-text mt-2 mb-2">
        {{ result.features.length }} loci drawn on the {{ result.map }}
        chromosome coordinates.
      </p>
      <div class="map-scroll">
        <svg
          ref="svg"
          xmlns="http://www.w3.org/2000/svg"
          :width="layout.width"
          :height="layout.height"
          :viewBox="`0 0 ${layout.width} ${layout.height}`"
          role="img"
          :aria-label="`${shownTitle}: ${result.features.length} loci on 5 chromosomes`"
          font-family="Arial, Helvetica, sans-serif"
          font-size="11"
        >
          <rect :width="layout.width" :height="layout.height" fill="#ffffff" />
          <g v-for="c in layout.chromosomes" :key="c.chr">
            <text :x="c.x" y="16" text-anchor="middle" font-weight="bold" fill="#000000">
              {{ c.chr }}
            </text>
            <rect
              :x="c.x - radius"
              :y="top"
              :width="radius * 2"
              :height="c.cen"
              :rx="radius"
              :fill="chrFill"
            />
            <rect
              :x="c.x - radius"
              :y="top + c.cen"
              :width="radius * 2"
              :height="c.len - c.cen"
              :rx="radius"
              :fill="chrFill"
            />
          </g>
          <g v-for="f in layout.features" :key="f.key">
            <line
              v-if="f.showLabel"
              :x1="f.x + radius"
              :y1="f.y"
              :x2="f.x + 5 * radius"
              :y2="f.labelY"
              stroke="#999999"
              stroke-width="0.5"
            />
            <text
              v-if="f.showLabel"
              :x="f.x + 5 * radius + 2"
              :y="f.labelY + 4"
              fill="#000000"
            >
              {{ f.label }}
            </text>
            <a :href="f.href" target="_blank" rel="noopener">
              <title>{{ f.locus }} ({{ f.start.toLocaleString() }})</title>
              <rect
                :x="f.x - 3"
                :y="f.y"
                width="7"
                height="2"
                :fill="f.color"
              />
            </a>
          </g>
        </svg>
      </div>

      <div v-if="result.notFound.length" class="mt-3">
        <b>Not found ({{ result.notFound.length }}):</b>
        {{ result.notFound.join(", ") }}
      </div>
    </div>
  </div>
</template>

<script>
import PostsService from "@/services/PostsService";

// Mirrors the v2 ViewChromosomes servlet: 125 px between chromosomes, 5 px
// radius, labels spaced at least one font height apart.
const CHR_DIST = 125;
const RADIUS = 5;
const TOP = 30;
const LABEL_STEP = 12;
const BP_PER_PX_AT_ZOOM_1 = 5000000;
const MAX_LOCI = 2000;

const CHR_COLORS = {
  GREEN: "rgb(50,150,50)",
  SAUSAGE: "rgb(255,128,128)",
  GRAY: "rgb(100,100,100)",
  LIGHTGRAY: "rgb(200,200,200)",
  BLACK: "rgb(0,0,0)",
};
const TICK_COLORS = {
  WHITE: "rgb(255,255,255)",
  BLACK: "rgb(0,0,0)",
  GRAY: "rgb(100,100,100)",
  YELLOW: "rgb(255,255,0)",
};

// v2's expression colouring: truncated value scaled by 50 on one channel.
function variableColor(value) {
  const v = Math.trunc(value);
  let red = 100;
  let green = 100;
  if (value < 0) green = v * -50;
  if (value > 0) red = v * 50;
  return `rgb(${red},${green},100)`;
}

// Pixel layout. Per chromosome, labels go in start order and are pushed down
// so consecutive labels are LABEL_STEP apart; tick-only features (label ".")
// take no label slot.
function layoutMap(result, zoom, tickColor) {
  const scale = BP_PER_PX_AT_ZOOM_1 / zoom;
  const chromosomes = result.chromosomes.map((c) => ({
    chr: c.chr,
    x: c.chr * CHR_DIST - CHR_DIST / 2,
    len: Math.floor(c.length / scale),
    cen: Math.floor(c.centromere / scale),
  }));
  const features = [];
  let bottom = Math.max(...chromosomes.map((c) => c.len)) + TOP;
  for (const c of chromosomes) {
    const onChr = result.features
      .filter((f) => f.chr === c.chr)
      .sort((a, b) => a.start - b.start);
    let lastLabel = -Infinity;
    for (const f of onChr) {
      const y = TOP + Math.floor(f.start / scale);
      const showLabel = f.label !== ".";
      let labelY = y;
      if (showLabel) {
        labelY = Math.max(y, lastLabel + LABEL_STEP);
        lastLabel = labelY;
        bottom = Math.max(bottom, labelY);
      }
      features.push({
        key: `${f.chr}:${f.locus}:${features.length}`,
        locus: f.locus,
        label: f.label,
        start: f.start,
        x: c.x,
        y,
        labelY,
        showLabel,
        href: `${window.location.origin}/locus?name=${encodeURIComponent(f.locus)}`,
        color:
          tickColor === "VARIABLE"
            ? variableColor(f.value)
            : TICK_COLORS[tickColor] || TICK_COLORS.GRAY,
      });
    }
  }
  return {
    chromosomes,
    features,
    width: result.chromosomes.length * 150,
    height: bottom + 30,
  };
}

const defaultForm = () => ({
  input: "",
  zoom: 100,
  title: "",
  chrColor: "GREEN",
  tickColor: "WHITE",
});

export default {
  name: "ChromosomeMap",
  data() {
    return {
      form: defaultForm(),
      inputPlaceholder: "At1g01010\nAt2g01008 hello\nAt5g67640 .\nAt3g10000 2.5",
      chrColorOptions: [
        { text: "green", value: "GREEN" },
        { text: "sausage", value: "SAUSAGE" },
        { text: "gray", value: "GRAY" },
        { text: "light gray", value: "LIGHTGRAY" },
        { text: "black", value: "BLACK" },
      ],
      tickColorOptions: [
        { text: "white", value: "WHITE" },
        { text: "black", value: "BLACK" },
        { text: "gray", value: "GRAY" },
        { text: "yellow", value: "YELLOW" },
        { text: "variable", value: "VARIABLE" },
      ],
      loading: false,
      errorMessage: "",
      result: null,
      // Display options are frozen at submit so the drawing matches the data.
      shown: defaultForm(),
      maxLoci: MAX_LOCI,
      radius: RADIUS,
      top: TOP,
    };
  },
  computed: {
    shownTitle() {
      return this.shown.title.trim() || "Arabidopsis Chromosomes 1-5";
    },
    chrFill() {
      return CHR_COLORS[this.shown.chrColor] || CHR_COLORS.SAUSAGE;
    },
    layout() {
      return layoutMap(this.result, this.shown.zoom, this.shown.tickColor);
    },
    statusMessage() {
      if (this.loading) return "Drawing chromosome map, please wait.";
      if (this.errorMessage) return `Chromosome map failed: ${this.errorMessage}`;
      if (this.result) {
        return `Chromosome map ready. ${this.result.features.length} loci drawn, ${this.result.notFound.length} not found.`;
      }
      return "";
    },
  },
  methods: {
    handleFileUpload(event) {
      const file = event.target.files[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          this.form.input = e.target.result;
        };
        reader.readAsText(file);
      }
    },
    handleReset() {
      this.form = defaultForm();
      this.result = null;
      this.errorMessage = "";
      if (this.$refs.fileInput) this.$refs.fileInput.value = "";
    },
    async handleSubmit() {
      this.errorMessage = "";
      this.result = null;
      if (!this.form.input.trim()) {
        this.errorMessage = "Please enter locus identifiers, or upload a file.";
        return;
      }
      const zoom = Number(this.form.zoom);
      if (!Number.isInteger(zoom) || zoom < 1 || zoom > 1000) {
        this.errorMessage = "Zoom factor must be a whole number from 1 to 1000.";
        return;
      }
      this.loading = true;
      try {
        const response = await PostsService.runChromosomeMap({
          input: this.form.input,
        });
        this.shown = { ...this.form, zoom };
        this.result = response.data;
      } catch (err) {
        this.errorMessage =
          (err.response && err.response.data && err.response.data.error) ||
          "The chromosome map could not be drawn. Please try again.";
      } finally {
        this.loading = false;
      }
    },
    svgMarkup() {
      return new XMLSerializer().serializeToString(this.$refs.svg);
    },
    saveBlob(blob, name) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = name;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    },
    downloadSvg() {
      this.saveBlob(
        new Blob([this.svgMarkup()], { type: "image/svg+xml;charset=utf-8" }),
        "chromosome_map.svg"
      );
    },
    downloadPng() {
      const { width, height } = this.layout;
      const scale = 2;
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = width * scale;
        canvas.height = height * scale;
        const ctx = canvas.getContext("2d");
        ctx.scale(scale, scale);
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob((blob) => this.saveBlob(blob, "chromosome_map.png"));
      };
      img.src =
        "data:image/svg+xml;charset=utf-8," + encodeURIComponent(this.svgMarkup());
    },
  },
};
</script>

<style scoped>
.mainheader {
  font-size: 16pt;
  font-weight: bold;
  color: #930;
  margin: 0;
}
.fieldset-title {
  font-size: 1rem;
  font-weight: bold;
  width: auto;
  text-align: left;
  padding: 0 0.25rem;
  margin: 0 0 0.75rem 1.25rem;
}
.note-text {
  color: #495057;
}
.map-scroll {
  overflow: auto;
}
</style>
